import React, { useState, ReactElement, useEffect } from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../types/state/storeTypes';
import { CollectionVariantDownload } from '../../types/api/DownloadsTypes';
import NoSSR from 'react-no-ssr';
import EmailNotVerifiedMessage from '../shared/EmailNotVerifiedMessage';
import { userIsNotConfirmed } from '../../helpers/user';
import EmailNotVerifiedDialog from '../shared/EmailNotVerifiedDialog';
import getCollectionDownloads from '../../hooks/getCollectionDownloads';
import { productIs3D } from '../../helpers/productHelpers';
import DownloadButton from '../products/DownloadButton';
import { FreeProductMarker } from '../shared/FreeProductMarker';
import { checkLegitimateOwnership } from '../../helpers/licenseUtils';

interface Ownership {
  [key: string]: {
    has_legitimate_license: boolean;
    downloads?: CollectionVariantDownload[];
  };
}

function CollectionOwnership(props: {
  collection: any;
  subscription: any;
  user: any;
  ownership: Ownership;
  products?: any[];
}) {
  const {
    collection,
    user,
    ownership,
    // optional prop for 3D collections with a single product
    // videos will be downloaded using that product
    products,
  } = props;

  const [disableDownloadButton, setDisableDownloadButton] = useState<boolean>(false);

  useEffect(() => {
    const hasNonLegitimateLicense = checkLegitimateOwnership(ownership);
    setDisableDownloadButton(hasNonLegitimateLicense);
  }, [ownership]);

  const getSingleProductDownloads = () => {
    if (!products || !products[0]) return;
    const productOwnership = ownership[products[0].id];
    return productOwnership?.downloads;
  };

  const [emailNotVerifiedDialogOpen, setEmailNotVerifiedDialogOpen] = useState<boolean>(false);

  const displaySingleProductDownload = (): boolean => {
    /* Workaround for downloading 3D collections with just one asset */
    return products.length === 1 && products[0] && productIs3D(collection);
  };

  const displayDownloadsAvailableBelowMessage = (): boolean => {
    return collection.library?.id === 3 && !collection.free_for_member && products.length > 1;
  }

  const renderDownloadsAvailableBelowMessage = (): ReactElement => {
    return (
      <div className="rounded-[5px] bg-gray-1000 text-white text-[16px] p-4 my-8">
        Downloads available below
      </div>
    );
  }

  const render = (): ReactElement => {
    return (
      <div className="pt-8">
        {collection.free_product &&
          <div className="">
            <h4 className="py-6 font-bold text-[#24C1FF] text-[32px] inter">
              FREE
            </h4>
            <FreeProductMarker user={user} />
          </div>
        }

        {collection.library?.id == 3 &&
          collection.free_for_member &&
          !displaySingleProductDownload() && (
            <div className="rounded-[5px] bg-gray-1000 text-white font-bold text-[16px] p-4 my-8">
              Access your assets on the{' '}
              <a href={`/accounts/downloads/${collection.id}`} className="text-a-blue">
                downloads page
              </a>
              .
            </div>
          )}

        {
          displayDownloadsAvailableBelowMessage() &&
          renderDownloadsAvailableBelowMessage()
        }

        {userIsNotConfirmed(user) && (
          <div className="rounded-[5px] bg-gray-1000 text-white font-bold text-[16px] p-4 my-8">
            <EmailNotVerifiedMessage />
          </div>
        )}

        {collectionDownloads &&
          (collectionDownloads?.length > 0 || displaySingleProductDownload()) && (
            <div className="relative w-full">
              <DownloadButton
                mode='collection-page'
                downloadedItem={displaySingleProductDownload() ? products[0] : collection}
                downloads={displaySingleProductDownload() ? getSingleProductDownloads() : collectionDownloads}
                singleProductCollectionDownload={displaySingleProductDownload()}
                disableDownloadButton={disableDownloadButton}
                collectionId={collection.id}
              />
            </div>
          )}
        <EmailNotVerifiedDialog
          isOpen={emailNotVerifiedDialogOpen}
          setIsOpen={setEmailNotVerifiedDialogOpen}
        />
      </div>
    );
  };

  // return early to not load downloads
  if (!ownership) return render();

  const { data: collectionDownloads } = getCollectionDownloads(collection, user);
  return render();
}

const mapStateToProps = (state: ApplicationState) => ({
  user: state.auth.user,
  subscription: state.auth.subscription
});

const ConnectedCollectionOwnership = connect(mapStateToProps)(CollectionOwnership);

export default (props) => (
  <NoSSR>
    <ConnectedCollectionOwnership {...props} />
  </NoSSR>
);
